import { DotLottiePlayer } from '@dotlottie/react-player';
import {
  createContext,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import AA, { AASTEPS } from './Components/AA';
import { loadPBAA, loadYBAA, loadAUAA } from './Components/AA/AAWrapper';
import Toast from './Components/JFS/Common/Toast';
import ConsentErrorSheet from './Components/AUBank/CreditHealthDashboard/components/ConsentErrorSheet';
import NativeLoader from './Components/NativeLoader';
import Nudge from './Models/Nudge';
import { ParentAppInterface } from './ParentAppInterface';
import { getLottiePath } from './Utils/assetUtils';
import { sendSessionId } from './Services/PFMClient/DashboardService';
import './Theme/default.css';
import {
  FIP,
  FlowType,
  isLogEnabled,
  SdkFlowSelection,
} from './Utils/constants';
import {
  EVENTCODES,
  pushEventToParent,
  pushEventToPosthog,
} from './Utils/eventUtils';
import { initializePostHog } from './Utils/postHogUtils';
import { ROUTES } from './Utils/routes';
import { captureException } from './Utils/sentryUtils';
import {
  getAltJourney,
  getAltJourneyTheme,
  getMahindraThemeMode,
  getTenant,
  getTrackingId,
  setAccessToken,
  setAltJourney,
  setAltJourneyTheme,
  setIsLoanJourney,
  setIsNewUser,
  setMahindraThemeMode,
  setRefreshToken,
  setSdkFlowSelection,
  setSessionId,
  setTenant,
  setTrackingId,
  setUserId,
  setIsWhiteListed,
  setMaskedMobileNumber,
  setUserProfile,
} from './Utils/storageUtils';
import { retryAsync } from './Utils/utils';
import {
  altJourneyTheme,
  altJourneyType,
  LOADERTYPE,
  TenantType,
} from './Utils/types';

import { VIEWTYPE } from './Components/HomePage';
export const AppContext = createContext({});
export const AppBridge = ParentAppInterface.getInstance();

export enum DASHBOARDPAGES {
  HOME,
  SPENDS,
  PORTFOLIO,
  CALENDAR,
  LIABILITIES,
  GOALDETAILS,
}

export enum ScreenType {
  DASHBOARD = 'dashboard',
  INCOME = 'income',
  EXPENDITURE = 'expenditure',
  INVESTMENT = 'investment',
}

export enum RefreshStatus {
  COMPLETED,
  NO_PROGRESS,
  REFRESH_AVAILABLE,
  PARTIAL_COMPLETED,
  IN_PROGRESS,
  FAILURE,
}

type statusData = {
  status: any;
  rawDataFetched: boolean;
  userConsentActive: boolean;
  approvedConsentsAvailable: boolean;
  accountDetails: any[];
  lastRefreshStatus: string;
  refreshAvailable: boolean;
  nextRefreshTime: string;
};

export type ContextType = {
  phoneNumber: string;
  fipIds: string[];
  isLoading: boolean;
};

const Dashboard = lazy(() => {
  return import('./Components/Dashboard');
});

const ETBCustomer = lazy(() => {
  return import('./Components/ETBCustomer');
});

const JFSBankAccountDetail = lazy(() => {
  return import('./Components/JFS/BankAccountDetail');
});

const JFSAllTransactions = lazy(() => {
  return import('./Components/JFS/AllTransactions');
});

const JFSCategoryTransactions = lazy(() => {
  return import('./Components/JFS/CategoryTransactions');
});

const JFSTransactionHistory = lazy(() => {
  return import('./Components/JFS/TransactionHistory');
});
const JFSWelcomeScreen = lazy(() => {
  return import('./Components/JFS/WelcomeScreen');
});

const AUWelcomeScreen = lazy(() => {
  return import('./Components/AUBank/WelcomeScreen');
});

const AUSpends = lazy(() => {
  return import('./Components/AUBank/Spends');
});

const AUStocks = lazy(() => {
  return import('./Components/AUBank/StocksNewFlow');
});

const AUNudges = lazy(() => {
  return import('./Components/AUBank/NudgesScreenDashboard');
});

const AUFDRDDetails = lazy(() => {
  return import('./Components/AUBank/FDRDDetails');
});

const AUEPFDetails = lazy(() => {
  return import('./Components/AUBank/EPFDetails');
});

const AUEPFPassbook = lazy(() => {
  return import('./Components/AUBank/EPFDetails/EPFPassbook');
});

const AUAllEmployers = lazy(() => {
  return import('./Components/AUBank/EPFDetails/AllEmployers');
});

const AUEPFAccountDetails = lazy(() => {
  return import('./Components/AUBank/EPFDetails/AccountDetails');
});

const AUMutualFunds = lazy(() => {
  return import('./Components/AUBank/MutualFunds');
});

const StocksETFs = lazy(() => {
  return import('./Components/JFS/StocksETFs');
});

const MutualFunds = lazy(() => {
  return import('./Components/JFS/MutualFunds');
});

const FAQ = lazy(() => {
  return import('./Components/JFS/FAQ');
});

const JFSAccountDetail = lazy(() => {
  return import('./Components/JFS/AccountDetail');
});

const JFSDataFailed = lazy(() => {
  return import('./Components/JFS/DataFailed');
});

const JFSNudgeCardsPage = lazy(() => {
  return import('./Components/JFS/NudgeCardsPage');
});

const JFSNudges = lazy(() => {
  return import('./Components/JFS/NudgeStack/JFSNudgeStackPage');
});

const YBTheme = lazy(() => import('./Components/Theme/YesBank'));
const YesGlory = lazy(() => import('./Components/Theme/YesOnline/yesGlory'));
const YesGrandure = lazy(
  () => import('./Components/Theme/YesOnline/yesGrandure')
);
const YesPremia = lazy(() => import('./Components/Theme/YesOnline/yesPremia'));
const YesFirst = lazy(() => import('./Components/Theme/YesOnline/yesFirst'));
const YesProsperity = lazy(
  () => import('./Components/Theme/YesOnline/yesProsperity')
);
const YesPrivate = lazy(
  () => import('./Components/Theme/YesOnline/yesPrivate')
);

const PBTheme = lazy(() => import('./Components/Theme/PB'));
const AUTheme = lazy(() => import('./Components/Theme/AU'));
const MFTheme_Light = lazy(
  () => import('./Components/Theme/Mahindra/MF_lightMode')
);
const MFTheme_Dark = lazy(
  () => import('./Components/Theme/Mahindra/Mf_darkMode')
);
const JFSTheme = lazy(() => import('./Components/Theme/JFS'));

let eventSendOnLoad = false;

const App: React.FC = () => {
  const [statusPolling, setStatusPolling] = useState(0);
  const [isKeyInshigtsVisible, setIsKeyInshigtsVisible] =
    useState<boolean>(false);
  const [selectedNudge, setSelectedNudge] = useState<any>();
  const urlParams = new URLSearchParams(window.location.search);
  const [currentRoute, setCurrentRoute] = useState<any>(null);
  const [previousRoute, setPreviousRoute] = useState<any>(null);
  const [loaderType, setLoaderType] = useState(LOADERTYPE.GENERIC);
  const [isLoading, setIsLoading] = useState(true);
  const [analyticsStatus, setAnalyticsStatus] = useState<string>('PENDING');
  const [analyticsStatusStocks, setAnalyticsStatusStocks] =
    useState<string>('PENDING');
  const [isAdditionalAccountJourney, setIsAdditionalAccountJourney] =
    useState<boolean>(false);
  const [isAccountDetailsFetched, setIsAccountDetailsFetched] = useState(false);
  const [isAnyDataFetched, setIsAnyDataFetched] = useState(false);
  const deviceTypeRef = useRef('MOBILE');
  const [goBackEventReceived, setGoBackEventReceived] = useState(false);
  const [hidePopupNudge, setHidePopupNudge] = useState(false);
  const [currentStepLoader, setCurrentStepLoader] = useState(1);
  const [dataRefreshStatus, setDataRefreshStatus] = useState<RefreshStatus>(
    RefreshStatus.NO_PROGRESS
  );
  const [customFailureMessage, setCustomFailureMessage] = useState('');
  const [AAInternalStep, setAAInternalStep] = useState<AASTEPS | null>(
    AASTEPS.AA_JOURNEY
  );
  const [showToastAA, setShowToastAA] = useState(false);
  const [showAUErrorSheet, setShowAUErrorSheet] = useState(false);
  const [refreshToastText, setRefreshToastText] = useState('');
  const [forceRender, setForceRender] = useState(0);

  const dataRefreshStatusRef = useRef<any>(RefreshStatus.NO_PROGRESS);
  const [selectedFIP, setSelectedFIP] = useState<string[]>([]);
  const [partialLoading, setPartialLoading] = useState(false);
  const [shouldThrow, setShouldThrow] = useState<Error | null>(null);
  const [ViewType, setViewType] = useState<VIEWTYPE>(VIEWTYPE.ASSETSVIEW);
  const [currentFlowType, setCurrentFlowType] = useState<FlowType>(
    FlowType.BANKS
  );
  const isPrefetchSpends = useRef<boolean>(false);
  const [monthlyPaymentsVisible, setMonthlyPaymentsVisible] = useState(false);
  const isLoanJourney = useRef(false);
  const isBackEventSent = useRef(false);
  const isLoaderBackEventSent = useRef(false);
  const hasPushedEvent = useRef(false);
  const [isAmountVisible, setIsAmountVisible] = useState<boolean>(true);
  const [isHeaderVisible, setIsHeaderVisible] = useState<boolean>(false);
  const [isBottomNavBarVisible, setIsBottomNavbarVisible] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const otherDepositBalance = useRef<number>(0);
  const [FIPs, setFIPs] = useState<FIP[]>([]);
  const isPollingInProgress = useRef<boolean>(false);
  const isYesBankOnlyJourney = useRef<boolean>(false);
  const [title, setTitle] = useState<string[]>(['PB Money']);
  const [
    updateStatusPollingAnalyticsStatus,
    setUpdateStatusPollingAnalyticsStatus,
  ] = useState(0);
  const datafromAnalyticsStatus = useRef(null);
  const [criticalDataStatus, setCriticalDataStatus] = useState({
    rawData: false,
    overviewAnalysis: false,
  });
  const isLoadingRef = useRef(isLoading);
  const isMetaProcessed = useRef(false);
  // Store the initial deep link pathname when journey opens
  const initialDeepLinkPath = useRef(window.location.pathname);
  // Track if deep link has been handled (to prevent re-routing)
  const deepLinkHandled = useRef(false);
  const isJFSTenant = process.env.REACT_APP_TENANT_ID_JFS !== undefined;

  const [backButtonHandler, setBackButtonHandler] = useState<any>(() => {
    return () => {
      if (isJFSTenant) {
        AppBridge.closeWebview();
      }
    };
  });

  useEffect(() => {
    if (isJFSTenant) {
      (window as any).handleHardwareBackButton = (...args: any[]) => {
        backButtonHandler(...args);
      };
    }
  }, [backButtonHandler]);

  // Centralized handleIframeBackAction listener for PaisaBazaar
  useEffect(() => {
    const handleIframeBackAction = (event: MessageEvent) => {
      try {
        let parsedData;

        // Handle both string and object data
        if (typeof event.data === 'string') {
          parsedData = JSON.parse(event.data);
        } else if (event.data && typeof event.data === 'object') {
          parsedData = event.data;
        }

        // Check if the action is handleIframeBackAction
        if (parsedData && parsedData.action === 'handleIframeBackAction') {
          // Call the current back button handler
          backButtonHandler();
        }
      } catch (error) {
        captureException(error);
        // Silently ignore parsing errors
      }
    };

    window.addEventListener('message', handleIframeBackAction);

    return () => {
      window.removeEventListener('message', handleIframeBackAction);
    };
  }, [backButtonHandler]);

  //TODO Call API to get meta Using JWTMETA
  const handleGetJwt = async () => {
    try {
      const result: any = await AppBridge.getJwtAuthToken();
      AppBridge.startBackHandling();

      // Handle different JWT token response formats
      let token: string = '';
      let parsedResult = result;

      // Check if result is a JSON string and parse it
      if (typeof result === 'string') {
        try {
          parsedResult = JSON.parse(result);
        } catch (e) {
          // If parsing fails, treat as direct JWT token string
          token = result;
        }
      }

      // If we haven't assigned token yet, check parsed result
      if (!token) {
        if (parsedResult.data && parsedResult.data.jwtToken) {
          // Format: { data: { jwtToken: "..." } }
          token = parsedResult.data.jwtToken;
        } else if (parsedResult.jwtToken) {
          // Format: { jwtToken: "..." }
          token = parsedResult.jwtToken;
        } else if (typeof parsedResult === 'string') {
          // Format: Direct JWT token string
          token = parsedResult;
        } else {
          throw new Error('Invalid JWT response format - token not found');
        }
      }

      // Debug: Show the full URL being called
      const url = process.env.REACT_APP_PFM_JWT_BASEURL + '/initiate';

      // Wrap the initiate API call with retry mechanism
      const response = await retryAsync(
        async () => {
          const res = await fetch(url, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          // If response is not ok, throw error to trigger retry
          if (!res.ok) {
            throw new Error(`Initiate API failed with status: ${res.status}`);
          }

          return res;
        },
        3, // maxRetries - retry up to 3 times
        500, // delay - 1 second initial delay with exponential backoff
        (error: any) => {
          // Retry on network errors (TypeError, fetch failures)
          if (error instanceof TypeError) {
            return true;
          }

          // Retry on 5xx server errors
          if (error.message && error.message.includes('status:')) {
            const status = parseInt(error.message.split('status:')[1]);
            return status >= 500; // Only retry on server errors (500, 502, 503, etc.)
          }

          // Retry on other network-related errors
          return true;
        }
      );

      if (response.ok) {
        const res = await response.text();

        if (res) {
          try {
            const responseBody = JSON.parse(res);

            processMeta({ meta: responseBody.meta });
          } catch (e) {
            captureException(e);
          }
        }
      } else {
        AppBridge.closeWebview();
      }

      // alert('JWT: ' + result);
      // Assuming the returned object has a token field
    } catch (err) {
      captureException(err);
      if (process.env.REACT_APP_IS_UAT_ENV === 'true') {
        console.error(err);
      } else {
        const jwtError =
          err instanceof Error
            ? new Error(`JWT_FAILURE: ${err.message}`)
            : new Error(`JWT_FAILURE: ${String(err)}`);
        // Preserve the original error stack if available
        if (err instanceof Error && err.stack) {
          jwtError.stack = err.stack;
        }
        setShouldThrow(jwtError);
      }
    }
  };
  const openSheet = () => {
    setIsSheetOpen(true);
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
  };

  useEffect(() => {
    dataRefreshStatusRef.current = dataRefreshStatus;
  }, [dataRefreshStatus]);

  const errorCreateConsent = () => {
    setIsLoading(false);
    if (getTenant() !== TenantType.JFS && getTenant() !== TenantType.AU) {
      setCurrentRoute(ROUTES.ERROR);
    }
    if (getTenant() !== TenantType.AU) {
      pushEventToParent({ eventCode: 'SESSION_TIMEOUT' });
    }
  };
  useEffect(() => {
    // Only handle JWT for JFS tenant
    const isJFS = process.env.REACT_APP_TENANT_ID_JFS !== undefined;
    if (isJFS) {
      handleGetJwt();
    }
  }, []);
  useEffect(() => {
    isLoadingRef.current = isLoading;
    if (isLoading) {
      window.scrollTo(0, 0);
    }
  }, [isLoading]);

  const documentHeight = () => {
    const doc = document.documentElement;
    doc.style.setProperty(
      '--doc-height',
      `${
        window.innerHeight ||
        document.documentElement.clientHeight ||
        document.body.clientHeight
      }px`
    );
  };

  window.addEventListener('resize', () => documentHeight());
  documentHeight();

  const renderCurrentRoute = () => {
    if (currentRoute === ROUTES.AAJOURNEY) {
      return <AA isError={errorCreateConsent} />;
    } else if (currentRoute === ROUTES.DASHBOARD) {
      return <Dashboard setCurrentRoute={setCurrentRoute} />;
    } else if (currentRoute === ROUTES.ETBCUSTOMERSCREEN) {
      return <ETBCustomer />;
    } else if (currentRoute === ROUTES.JFS_BANK_ACCOUNT_DETAIL) {
      return <JFSBankAccountDetail />;
    } else if (currentRoute === ROUTES.JFS_ALL_TRANSACTIONS) {
      return <JFSAllTransactions />;
    } else if (currentRoute === ROUTES.JFS_CATEGORY_TRANSACTIONS) {
      return <JFSCategoryTransactions />;
    } else if (currentRoute === ROUTES.JFS_TRANSACTION_HISTORY) {
      return <JFSTransactionHistory />;
    } else if (currentRoute === ROUTES.JFS_WELCOME) {
      return <JFSWelcomeScreen />;
    } else if (currentRoute === ROUTES.AU_WELCOME) {
      return <AUWelcomeScreen />;
    } else if (currentRoute === ROUTES.AU_SPENDS) {
      return <AUSpends />;
    } else if (currentRoute === ROUTES.AU_STOCKS) {
      return <AUStocks />;
    } else if (currentRoute === ROUTES.AU_NUDGES) {
      return <AUNudges />;
    } else if (currentRoute === ROUTES.AU_FDRD) {
      return <AUFDRDDetails />;
    } else if (currentRoute === ROUTES.AU_EPF) {
      return <AUEPFDetails />;
    } else if (currentRoute === ROUTES.AU_EPF_PASSBOOK) {
      return <AUEPFPassbook />;
    } else if (currentRoute === ROUTES.AU_EPF_ALL_EMPLOYERS) {
      return <AUAllEmployers />;
    } else if (currentRoute === ROUTES.AU_EPF_ACCOUNT_DETAILS) {
      return <AUEPFAccountDetails />;
    } else if (currentRoute === ROUTES.AU_MUTUAL_FUNDS) {
      return <AUMutualFunds />;
    } else if (currentRoute === ROUTES.STOCKS_ETFS) {
      return <StocksETFs />;
    } else if (currentRoute === ROUTES.MUTUAL_FUNDS) {
      return <MutualFunds />;
    } else if (currentRoute === ROUTES.JFS_FAQ) {
      return <FAQ />;
    } else if (currentRoute === ROUTES.JFS_ACCOUNT_DETAIL) {
      return <JFSAccountDetail />;
    } else if (currentRoute === ROUTES.JFS_NUDGE_CARDS) {
      return <JFSNudgeCardsPage />;
    } else if (currentRoute === ROUTES.JFS_NUDGES) {
      return <JFSNudges />;
    } else if (currentRoute === ROUTES.JFS_DATA_FAILED) {
      return <JFSDataFailed />;
    } else if (currentRoute === ROUTES.ERROR) {
      return <div>Something went wrong</div>;
    }
  };

  const navigateToDashboard = () => {
    setCurrentRoute(ROUTES.DASHBOARD);
  };

  useEffect(() => {
    if (isLoading && getTenant() === TenantType.PB) {
      setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
          setLoaderType(LOADERTYPE.GENERIC);
        }
      }, 30000);
    }
  }, [loaderType]);

  const startAdditionalAccountJourney = (journeyType: FlowType) => {
    setLoaderType(LOADERTYPE.GENERIC);
    setIsAdditionalAccountJourney(true);
    setCurrentFlowType(journeyType);
    setSelectedFIP([]);
    setCurrentRoute(ROUTES.AAJOURNEY);
    navigate('/add-account');
  };

  const navigate = useNavigate();

  const processMeta = (parsedData: any) => {
    // Prevent processing meta more than once
    if (isMetaProcessed.current) {
      return;
    }
    isMetaProcessed.current = true;

    if (isLogEnabled) {
      console.log('Metadata recieved');
    }
    pushEventToParent({ eventCode: EVENTCODES.METADATA_RECEIVED });
    const metaData = JSON.parse(atob(parsedData.meta));
    const deviceType = parsedData.deviceType;

    if (metaData.channelType === 'ONLINE') {
      setAltJourney(altJourneyType.YESONLINE);
      const customerSegmentCode = urlParams.get('brandid');

      const themeMapping: Record<
        string,
        (typeof altJourneyTheme)[keyof typeof altJourneyTheme]
      > = {
        '56881': altJourneyTheme.YesPrivate,
        '124233': altJourneyTheme.YesPropsperity,
        '56877': altJourneyTheme.YesFirst,
        '56880': altJourneyTheme.YesPremia,
        '56873': altJourneyTheme.YesGrandure,
        '56878': altJourneyTheme.YesGlory,
      };

      const selectedTheme =
        customerSegmentCode && themeMapping[customerSegmentCode]
          ? themeMapping[customerSegmentCode]
          : altJourneyTheme.YesGlory;

      setAltJourneyTheme(selectedTheme);
    }
    deviceTypeRef.current = deviceType;
    setUserId(metaData.userId);
    setAccessToken(metaData.accessToken);
    setRefreshToken(metaData.refreshToken);
    setIsWhiteListed(metaData.whiteListed ?? false);

    if (metaData.maskedMobileNumber) {
      setMaskedMobileNumber(metaData.maskedMobileNumber);
    }

    if (metaData.userProfile) {
      setUserProfile(metaData.userProfile);
    }

    if (!getTrackingId()) {
      setTrackingId(metaData.trackingId);
    }

    if (metaData && metaData.autoRefreshInitiated) {
      pushEventToParent({
        eventCode: EVENTCODES.AUTO_REFRESH,
      });
      setDataRefreshStatus(RefreshStatus.IN_PROGRESS);
    }

    if (metaData && metaData.sdkFlowSelection) {
      if (metaData.sdkFlowSelection === SdkFlowSelection.SHOW_FIP_SELECTION) {
        setSdkFlowSelection(true);
      }
    }

    if (metaData && metaData.tenantId && !getTenant()) {
      setTenant(metaData.tenantId);
    }

    if (getTenant() === TenantType.AU) {
      if (!metaData.approvedConsentsAvailable && !metaData.userConsentActive) {
        setCurrentRoute(ROUTES.AU_WELCOME);
        // navigate('/au-welcome');
        setIsLoading(false);
      } else {
        setIsNewUser(false);
        setLoaderType(LOADERTYPE.DATALOADER);
        setCurrentRoute(ROUTES.DASHBOARD);
        // Only set loading to false if no deep link or deep link is home/empty
        const deepLinkPath = initialDeepLinkPath.current;
        const hasDeepLink =
          deepLinkPath &&
          deepLinkPath !== '/' &&
          deepLinkPath !== '/home' &&
          !deepLinkPath.includes('/dashboard');
        if (!hasDeepLink) {
          setIsLoading(false);
        }
        // If there's a deep link, loading will be set to false after route change in HomePageNew
      }
      return;
    }

    // For JFS, check if new user
    if (getTenant() === TenantType.JFS) {
      // setIsBottomNavbarVisible(true);
      // setIsHeaderVisible(true);

      const isNewJFSUser =
        !metaData.userConsentActive && !metaData.approvedConsentsAvailable;

      if (isNewJFSUser) {
        setCurrentRoute(ROUTES.JFS_WELCOME);
        navigate('/welcome');
      } else {
        setIsNewUser(false);
        setCurrentRoute(ROUTES.DASHBOARD);
        navigate('/home');
      }
      return;
    }

    let flag;
    if (getTenant() === TenantType.PB) {
      flag = metaData.approvedConsentsAvailable;
    } else {
      flag = metaData.userConsentActive;
    }
    if (flag && !isLoanJourney.current) {
      pushEventToParent({
        eventCode: EVENTCODES.JOURNEY_STARTED_EXISTING_USER,
        ...(getTenant() === TenantType.PB
          ? {
              postHogEventName: EVENTCODES.JOURNEY_STARTED,
            }
          : {}),
      });
      setIsNewUser(false);
      setIsLoading(true);
      setCurrentRoute(ROUTES.DASHBOARD);
      // If approved consents are available, route directly to the Assets dashboard
      navigate('/home');
      setLoaderType(LOADERTYPE.DATALOADER);
    } else if (metaData.tenantId === TenantType.MAHINDRA) {
      setCurrentRoute(ROUTES.DASHBOARD);
      if (!metaData.userConsentActive) {
        setIsLoading(false);
      }
      navigate('/home');
    } else {
      pushEventToParent({
        eventCode: EVENTCODES.JOURNEY_STARTED_NEW_USER,
        ...(getTenant() === TenantType.PB
          ? {
              postHogEventName: EVENTCODES.JOURNEY_STARTED,
            }
          : {}),
      });

      navigate('/new-journey');
      setCurrentRoute(ROUTES.AAJOURNEY);
      if (getTenant() === TenantType.PB) {
        loadPBAA();
      } else if (
        getTenant() === TenantType.MAHINDRA ||
        getTenant() === TenantType.YESBANK
      ) {
        loadYBAA();
      } else if (getTenant() === TenantType.JFS) {
        import('./Components/AA/AAWrapper').then((module) =>
          module.loadJFSAA()
        );
      }
      setIsLoading(false);
    }
  };
  const sendSessionDataToSlack = (
    sessionId: string,
    trackingId: string | null
  ) => {
    if (getTenant() === TenantType.YESBANK) {
      try {
        fetch(
          'https://hooks.slack.com/services/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',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              text: `YBL_INTERNAL_UAT - TrackingID - ${
                trackingId || 'Not Available'
              }, SessionID - ${sessionId}`,
            }),
          }
        ).catch((error) => {
          console.error('Error sending session data to Slack:', error);
        });
      } catch (error) {
        captureException(error);
        console.error('Error in sendSessionDataToSlack:', error);
      }
    }
  };

  //Get Access Token and Refresh from Parent
  useEffect(() => {
    if (!eventSendOnLoad) {
      eventSendOnLoad = true;

      if (process.env.REACT_APP_TENANT_ID_MAHINDRA !== undefined) {
        const mahindraThemeMode = urlParams.get('theme');
        if (mahindraThemeMode === 'dark' || mahindraThemeMode === 'light') {
          setMahindraThemeMode(mahindraThemeMode);
        }
      }

      var userSessionId = urlParams.get('sessionId');
      if (userSessionId === null || userSessionId === undefined) {
        const rawSearch = window.location.search.slice(1);
        const match = rawSearch.match(/(?:^|&)token=([^&]*)/);
        if (match && match[1]) {
          userSessionId = match[1];
        }
      }
      if (userSessionId !== null && userSessionId !== undefined) {
        const queryParams = Array.from(urlParams.entries());

        pushEventToPosthog({
          eventCode: EVENTCODES.QUERY_PARAMS_INFO,
          meta: JSON.stringify(
            Object.fromEntries(
              queryParams.map(([key, value]: any) => [key, value.length])
            )
          ),
        });
        setSessionId(userSessionId);
        const trackingIdFromStorage = getTrackingId();

        try {
          if (process.env.REACT_APP_TENANT_ID_YB !== undefined) {
            sendSessionId(userSessionId);
          }
        } catch (ex) {
          captureException(ex);
          console.log(ex);
        }
        sendSessionDataToSlack(userSessionId, trackingIdFromStorage);
      } else {
        pushEventToPosthog({ eventCode: EVENTCODES.NO_SESSION_ID });
      }

      const trackingId: any = urlParams.get('trackingId');
      if (trackingId) {
        setTrackingId(trackingId);
      }

      if (urlParams.get('enabledOnlyAAJourney') === 'true') {
        setIsLoanJourney(true);
        isLoanJourney.current = true;
        pushEventToParent({
          eventCode: EVENTCODES.LOAN_JOURNEY,
        });
      } else {
        pushEventToParent({
          eventCode: EVENTCODES.PFM_JOURNEY,
        });
      }
    }

    window.addEventListener('message', (event) => {
      const { data } = event;

      if (data && data.action) {
        setGoBackEventReceived(true);
        if (data.action === 'handleIframeBackAction') {
          if (isLoadingRef.current && !isLoaderBackEventSent.current) {
            pushEventToParent({
              eventCode: EVENTCODES.BACK_BUTTON_PFMDB,
            });
            pushEventToParent({ eventCode: 'BACK_BUTTON_PRESSED' });
            isLoaderBackEventSent.current = true;
          }
        }
      }

      try {
        if (typeof data === 'string') {
          const parsedData = JSON.parse(data);

          if (parsedData.action) {
            setGoBackEventReceived(true);
            if (parsedData.action === 'handleIframeBackAction') {
              if (isLoadingRef.current && !isLoaderBackEventSent.current) {
                pushEventToParent({
                  eventCode: EVENTCODES.BACK_BUTTON_PFMDB,
                });
                pushEventToParent({ eventCode: 'BACK_BUTTON_PRESSED' });
                isLoaderBackEventSent.current = true;
              }
            }
          }

          if (parsedData.meta) {
            processMeta(parsedData);
          }
        }
      } catch {
        if (isLogEnabled) {
          console.log('Invalid meta');
        }
      }
    });

    pushEventToParent({ eventCode: 'JOURNEY_LOADED' });

    initializePostHog();

    window.onpopstate = () => {
      if (process.env.REACT_APP_TENANT_ID === 'ignosis') {
        window.onpopstate = () => {
          window.history.pushState(null, '', window.location.href);
        };
      }
      if (isLoadingRef.current && !isLoaderBackEventSent.current) {
        pushEventToParent({
          eventCode: EVENTCODES.BACK_BUTTON_PFMDB,
        });
        pushEventToParent({ eventCode: 'BACK_BUTTON_PRESSED' });
        isLoaderBackEventSent.current = true;
      }
    };
  }, [isLoading]);

  // Override browser back button behavior for ignosis tenant
  useEffect(() => {
    if (process.env.REACT_APP_TENANT_ID === 'ignosis') {
      window.onpopstate = () => {
        // Do nothing for ignosis tenant
        return;
      };
    }
  }, []);

  const [isCategoryAnalysisShown, setIsCategoryAnalysisShown] = useState(false);
  const isAssetDashboardRendered = useRef(false);
  const [goals, setGoals] = useState<any[]>([]);
  const [listOfItemsBudget, setListOfItemsBudget] = useState<any[]>([]);
  const [nudgeDrawerOpen, setNudgeDrawerOpen] = useState(false);
  const [drawerNudge, setDrawerNudge] = useState<Nudge | undefined>(undefined);
  const [currentMetaData, setCurrentMetaData] = useState<any>(undefined);
  const [isDetailedTransactionSheetOpen, setIsDetailedTransactionSheetOpen] =
    useState(false);
  const [nudges, setNudges] = useState([]);
  const [dismissedNudges, setDismissedNudges] = useState<string[]>([]);

  const [accountIds, setAccountIds] = useState([]);

  const data = useRef<any>({});
  const accountsData = useRef<any>({});
  const [stocksData, setStocksData] = useState<any[]>([]);
  const [termRecurringData, setTermRecurringData] = useState<any[]>([]);

  const analyticsStatusRef = useRef<string>('PENDING');
  const statusData = useRef<statusData>({
    accountDetails: [],
    userConsentActive: false,
    approvedConsentsAvailable: false,
    rawDataFetched: false,
    status: null,
    lastRefreshStatus: 'COMPLETED',
    refreshAvailable: false,
    nextRefreshTime: 'COMPLETED',
  });

  const [portfolioList, setPortfolioList]: any = useState<any>([
    'banks',
    'mutualFunds',
    'stocks',
    'deposits',
  ]);

  const onNudgeClick = (nudgeToDisplay: Nudge) => {
    if (nudgeToDisplay.id === '10' || nudgeToDisplay.id === '12') {
    } else {
      setDrawerNudge(nudgeToDisplay);
      setNudgeDrawerOpen(true);
    }
  };

  const onNudgeCtaClicked = () => {
    setNudgeDrawerOpen(false);
  };
  const location = useLocation();

  useEffect(() => {
    if (isLogEnabled) {
      console.log('location changes', location.pathname);
    }
  }, [location]);

  useEffect(() => {
    analyticsStatusRef.current = analyticsStatus;
  }, [analyticsStatus]);

  const getCurrentHeight = useCallback(() => {
    const navbarHeight = isHeaderVisible ? 58 : 0;
    const footerHeight = isBottomNavBarVisible ? 85 : 0;
    return 0;
  }, [isHeaderVisible, isBottomNavBarVisible]);

  const themeRender = () => {
    switch (getTenant()) {
      case TenantType.YESBANK:
        if (getAltJourney() === altJourneyType.YESONLINE) {
          switch (getAltJourneyTheme()) {
            case altJourneyTheme.YesGlory:
              return <YesGlory />;
            case altJourneyTheme.YesGrandure:
              return <YesGrandure />;
            case altJourneyTheme.YesPremia:
              return <YesPremia />;
            case altJourneyTheme.YesFirst:
              return <YesFirst />;
            case altJourneyTheme.YesPropsperity:
              return <YesProsperity />;
            case altJourneyTheme.YesPrivate:
              return <YesPrivate />;
            default:
              return <YesGlory />;
          }
        } else {
          return <YBTheme />;
        }
      case TenantType.MAHINDRA:
        return getMahindraThemeMode() === 'dark' ? (
          <MFTheme_Dark />
        ) : (
          <MFTheme_Light />
        );
      case TenantType.JFS:
        return <JFSTheme />;
      case TenantType.PB:
        return <PBTheme />;

      case TenantType.AU:
        return <AUTheme />;
      default:
        return <></>;
    }
  };

  useEffect(() => {
    if (getTenant() === TenantType.JFS) {
      const hideRoutes = [ROUTES.AAJOURNEY];
      const showRoutes = [
        ROUTES.DASHBOARD,
        ROUTES.JFS_WELCOME,
        ROUTES.JFS_BANK_ACCOUNT_DETAIL,
        ROUTES.JFS_ALL_TRANSACTIONS,
        ROUTES.JFS_CATEGORY_TRANSACTIONS,
        ROUTES.STOCKS_ETFS,
        ROUTES.MUTUAL_FUNDS,
        ROUTES.JFS_FAQ,
        ROUTES.JFS_ACCOUNT_DETAIL,
      ];
      if (hideRoutes.includes(currentRoute)) {
        setIsBottomNavbarVisible(false);
        setIsHeaderVisible(false);
      } else if (showRoutes.includes(currentRoute)) {
        // setIsBottomNavbarVisible(true);
        // setIsHeaderVisible(true);
      }
    }
  }, [currentRoute]);

  if (shouldThrow) {
    throw shouldThrow;
  }

  return (
    <AppContext.Provider
      value={{
        ViewType,
        setViewType,
        selectedFIP,
        setSelectedFIP,
        AAInternalStep,
        setAAInternalStep,
        navigateToDashboard,
        loading: isLoading,
        isLoading,
        setIsLoading,
        isLoanJourney,
        loaderType,
        analyticsStatus,
        setAnalyticsStatus,
        analyticsStatusStocks,
        setAnalyticsStatusStocks,
        setLoaderType,
        startAdditionalAccountJourney,
        currentFlowType,
        isKeyInshigtsVisible,
        setIsKeyInshigtsVisible,
        selectedNudge,
        setSelectedNudge,
        isAccountDetailsFetched,
        setIsAccountDetailsFetched,
        isAnyDataFetched,
        setIsAnyDataFetched,
        deviceTypeRef,
        setPortfolioList,
        portfolioList,
        currentMetaData,
        setCurrentMetaData,
        setIsDetailedTransactionSheetOpen,
        isDetailedTransactionSheetOpen,
        goals,
        setGoals,
        listOfItemsBudget,
        setListOfItemsBudget,
        nudgeDrawerOpen,
        setNudgeDrawerOpen,
        drawerNudge,
        setDrawerNudge,
        onNudgeClick,
        onNudgeCtaClicked,
        data,
        accountsData,
        analyticsStatusRef,
        setNudges,
        nudges,
        setAccountIds,
        accountIds,
        stocksData,
        termRecurringData,
        setTermRecurringData,
        setStocksData,
        statusData,
        setIsCategoryAnalysisShown,
        isCategoryAnalysisShown,
        statusPolling,
        setStatusPolling,
        currentStepLoader,
        setCurrentStepLoader,
        goBackEventReceived,
        isAdditionalAccountJourney,
        setIsAdditionalAccountJourney,
        isBackEventSent,
        isLoaderBackEventSent,
        monthlyPaymentsVisible,
        setMonthlyPaymentsVisible,
        dataRefreshStatus,
        setDataRefreshStatus,
        dataRefreshStatusRef,
        dismissedNudges,
        setDismissedNudges,
        hasPushedEvent,
        setCurrentRoute,
        previousRoute,
        setPreviousRoute,
        isAmountVisible,
        setIsAmountVisible,
        isHeaderVisible,
        setIsHeaderVisible,
        setIsBottomNavbarVisible,
        isSheetOpen,
        setIsSheetOpen,
        openSheet,
        closeSheet,
        partialLoading,
        setPartialLoading,
        otherDepositBalance,
        isPollingInProgress,
        FIPs,
        setFIPs,
        isYesBankOnlyJourney,
        hidePopupNudge,
        setHidePopupNudge,
        customFailureMessage,
        setCustomFailureMessage,
        isPrefetchSpends,
        updateStatusPollingAnalyticsStatus,
        setUpdateStatusPollingAnalyticsStatus,
        title,
        setTitle,
        setBackButtonHandler,
        getCurrentHeight,
        setCriticalDataStatus,
        criticalDataStatus,
        datafromAnalyticsStatus,
        setCurrentFlowType,
        forceRender,
        showToastAA,
        setShowToastAA,
        showAUErrorSheet,
        setShowAUErrorSheet,
        setForceRender,
        setRefreshToastText,
        refreshToastText,
        isAssetDashboardRendered,
        backButtonHandler,
        initialDeepLinkPath,
        deepLinkHandled,
      }}
    >
      <div
        className={`${
          currentRoute === ROUTES.DASHBOARD ? 'heightPercent' : 'heightVH'
        } App ${
          getAltJourney() === altJourneyType.YESONLINE ? 'yesonlineApp' : ''
        } ${getTenant() === TenantType.JFS ? 'jfs-tenant' : ''} ${
          getTenant() === TenantType.MAHINDRA ? 'mahindra-tenant' : ''
        }`}
      >
        {getTenant() === TenantType.JFS ? (
          <>
            {/* {isHeaderVisible ? <JioHeader /> : <></>} */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: `calc(100% - ${getCurrentHeight()}px`,
                overflow: 'auto',
              }}
            >
              {isLoading &&
              currentRoute &&
              currentRoute !== ROUTES.JFS_WELCOME ? (
                <NativeLoader loaderHeight={getCurrentHeight()} />
              ) : isLoading ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    width: '100%',
                    maxWidth: 'var(--max-width)',
                    height: `calc(100vh - ${getCurrentHeight()}px`,
                    background: '#fff',
                    zIndex: 1,
                  }}
                >
                  <DotLottiePlayer
                    src={getLottiePath('jfsLoader.lottie')}
                    autoplay
                    loop
                    style={{
                      height: 48,
                      width: 48,
                    }}
                  />
                </div>
              ) : (
                <></>
              )}
              {renderCurrentRoute()}
            </div>
            {/* {isBottomNavBarVisible ? (
              <JFSBottomNavigation activeTab='finances' />
            ) : (
              <></>
            )} */}
          </>
        ) : (
          <>
            {isLoading && currentRoute ? <NativeLoader /> : <></>}
            {renderCurrentRoute()}
          </>
        )}

        {showToastAA && (
          <Toast
            message='Something went wrong'
            duration={2000}
            onClose={() => setShowToastAA(false)}
          />
        )}
        {showAUErrorSheet && (
          <ConsentErrorSheet
            visible={showAUErrorSheet}
            onClose={() => setShowAUErrorSheet(false)}
            description="Apologies for the technical issues. Please retry in some time."
            showRetry={false}
          />
        )}
        {/* {showRefreshToast ? (
          <Toast
            message={refreshToastText}
            onClose={() => {}}
            autoClose={false}
          />
        ) : (
          <></>
        )} */}
      </div>

      {themeRender()}
    </AppContext.Provider>
  );
};

export default App;
